/**
 * AuditView — the case audit log (every tool call, approval, memory write, control) with the hash
 * chain check, and "Replay case", which re-runs the stored events at 4x in a read-only copy of the
 * stream (agentReplay; nothing is sent anywhere). Kinds show as labels ("Model call" for `serving`, one
 * per model call) and the kind chips above the table filter the rows; the chain check always covers
 * the whole log.
 */

import { useState } from 'react';
import { X, ShieldCheck, ShieldAlert, History, Square } from 'lucide-react';
import {
  clockOf, formatDuration, auditKindLabel, auditKindOptions, filterAuditEntries, auditSubject,
} from './agentUi.js';
import './AuditView.css';

function actorText(actor) {
  if (!actor) return '';
  return actor.name || actor.id || actor.kind || '';
}

export default function AuditView({
  entries = [], verified = null, onReplay, onStopReplay, replaying = false, onClose, loading = false, error = null,
}) {
  const [kindFilter, setKindFilter] = useState('all');
  const options = auditKindOptions(entries);
  // A filter whose kind no longer occurs (another case's log) falls back to every entry.
  const kind = kindFilter !== 'all' && options.some((o) => o.kind === kindFilter) ? kindFilter : 'all';
  const shown = filterAuditEntries(entries, kind);
  return (
    <section className="agent-panel audv" aria-label="Audit log">
      <header className="agent-panel__head">
        <div className="agent-panel__titles">
          <span className="agent-panel__kicker">Audit log</span>
          <h2 className="agent-panel__title">
            {kind === 'all' ? '' : `${shown.length} of `}{entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
          </h2>
        </div>
        <div className="audv__tools">
          {verified === true && (
            <span className="audv__chain is-good"><ShieldCheck size={12} aria-hidden="true" /> Hash chain verified</span>
          )}
          {verified === false && (
            <span className="audv__chain is-bad"><ShieldAlert size={12} aria-hidden="true" /> Hash chain broken</span>
          )}
          {replaying ? (
            <button type="button" className="agent-btn" onClick={onStopReplay}><Square size={12} aria-hidden="true" /> Stop replay</button>
          ) : (
            <button type="button" className="agent-btn" onClick={onReplay} disabled={typeof onReplay !== 'function'}>
              <History size={12} aria-hidden="true" /> Replay case
            </button>
          )}
          {onClose && <button type="button" className="agent-icon" onClick={onClose} aria-label="Close the audit log"><X size={14} aria-hidden="true" /></button>}
        </div>
      </header>
      <div className="agent-panel__body">
        {loading && <p className="audv__note">Loading the audit log…</p>}
        {error && <p className="audv__error" role="alert">{error}</p>}
        {options.length > 1 && (
          <div className="audv__filters" role="group" aria-label="Filter by kind">
            <button
              type="button"
              className={`audv__filter${kind === 'all' ? ' is-on' : ''}`}
              aria-pressed={kind === 'all'}
              onClick={() => setKindFilter('all')}
            >
              All <span className="audv__count">{entries.length}</span>
            </button>
            {options.map((o) => (
              <button
                key={o.kind}
                type="button"
                className={`audv__filter${kind === o.kind ? ' is-on' : ''}`}
                aria-pressed={kind === o.kind}
                onClick={() => setKindFilter(kind === o.kind ? 'all' : o.kind)}
              >
                {o.label} <span className="audv__count">{o.count}</span>
              </button>
            ))}
          </div>
        )}
        <table className="audv__table">
          <thead>
            <tr><th>Time</th><th>Kind</th><th>Actor</th><th>Tool or event</th><th>Status</th></tr>
          </thead>
          <tbody>
            {shown.map((e, i) => (
              <tr key={`${e.seq ?? i}-${e.hash ?? ''}`} className={`is-${e.kind}`}>
                <td className="audv__mono">{clockOf(e.ts)}</td>
                <td><span className="audv__kind" title={e.kind}>{auditKindLabel(e.kind)}</span></td>
                <td>{actorText(e.actor)}</td>
                <td>
                  <span className="audv__mono">{auditSubject(e)}</span>
                  {e.detail && <span className="audv__detail"> {e.detail}</span>}
                  {e.kind === 'approval' && e.data?.decision && (
                    <span className="audv__detail"> {e.data.decision}{e.data.approver?.name ? ` by ${e.data.approver.name}` : ''}{e.data.reason ? `: ${e.data.reason}` : ''}</span>
                  )}
                </td>
                <td>
                  <span className="audv__mono">{e.status || e.decision?.outcome || ''}</span>
                  {e.duration_ms ? <span className="audv__detail"> {formatDuration(e.duration_ms)}</span> : null}
                </td>
              </tr>
            ))}
            {!loading && shown.length === 0 && <tr><td colSpan={5} className="audv__note">No entries.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
